import { AfterViewInit, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { Subject, zip } from 'rxjs';
import { take, takeUntil, finalize } from 'rxjs/operators';
import { AuthService } from '../../../shared/services/auth.service';
import { UserService } from '../../../shared/services/user.service';
import { WorkspaceService } from '../../../shared/services/workspace.service';
import { EmitterService } from '../../../shared/services/emitter.service';
import { PermissionService } from '../../../shared/services/permissions.service';
import { ThemeService } from '../../../shared/services/theme.service';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { ActivityLogService } from '../../../shared/services/activity-log.service';
import { MtmBreadcrumbLink, MtmBreadcrumbService } from '../../../shared/services/mtm-breadcrumb.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { OverlayService } from '../../../shared/services/overlayService';
import { CryptoService } from '../../../shared/services/crypto.service';
import { GoogleAnalyticsService } from '../../../shared/services/google-analytics.service';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { WithPendingGuard } from '../../../shared/decorators';
import {
  ActiveWorkspaceUpdatedEvent,
  ActivityLogLevels,
  HeaderInfo,
  WorkspaceAvatarUpdatedEvent,
  WorkspaceDTO,
  WorkspaceUpdatedEvent,
} from '../../../shared/interfaces';
import { ROUTES, STORAGE_KEYS, TRANSLATION_KEYS, BREADCRUMB, ERROR_CODES, UI } from '../constants/workspace.constants';
import { User } from '@sentry/angular';
import { ENABLE_NEW_PERMISSION_UI_WORKSPACE } from '../../../shared/constants/toggle-new-permission';

@Component({
  selector: 'mtm-edit-workspace',
  templateUrl: './edit-workspace.component.html',
  styleUrl: './edit-workspace.component.scss',
  standalone: false,
})
export class EditWorkspaceComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();

  private readonly workspaceSignal = signal<any>({});
  workspaceId: string;
  private readonly tabIndexSignal = signal<number>(0);
  private readonly companyUsersSignal = signal<any[]>([]);

  get workspace(): any {
    return this.workspaceSignal();
  }
  set workspace(value: any) {
    this.workspaceSignal.set(value ?? {});
  }

  get tabIndex(): number {
    return this.tabIndexSignal();
  }
  set tabIndex(value: number) {
    this.tabIndexSignal.set(value ?? 0);
  }

  get companyUsers(): any[] {
    return this.companyUsersSignal();
  }
  set companyUsers(value: any[]) {
    this.companyUsersSignal.set(value ?? []);
  }

  authUser: User | null = null;
  activeWorkspace: WorkspaceDTO | null = null;

  isWorkspace: boolean = true;
  isProject: boolean = false;
  isUser: boolean = false;
  isPending: boolean = false;
  showProjectPermission: boolean = false;
  canUpdateWorkspace: boolean = false;
  canDeleteWorkspace: boolean = false;

  headerForm: UntypedFormGroup = null;

  /** Template-visible copy of the feature flag gating the new `mtm-account-page` permission UI. */
  readonly ENABLE_NEW_PERMISSION_UI_WORKSPACE: boolean = ENABLE_NEW_PERMISSION_UI_WORKSPACE;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly activityLogService: ActivityLogService,
    private readonly permissionService: PermissionService,
    private readonly workspaceService: WorkspaceService,
    private readonly userService: UserService,
    private readonly themeService: ThemeService,
    private readonly translatePipe: TranslatePipe,
    private readonly mtmBreadcrumbService: MtmBreadcrumbService,
    private readonly notificationService: NotificationService,
    private readonly cryptoService: CryptoService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly analyticService: AnalyticsService,
    private readonly overlayService: OverlayService
  ) {}

  ngOnInit() {
    this.authUser = this.authService.getAuthUser();
    if (!this.authUser) {
      this.navigateToEntry();
      return;
    }

    this.canUpdateWorkspace = this.permissionService.canUpdateWorkspace();
    this.canDeleteWorkspace = this.permissionService.canDeleteWorkspace();
    this.subscribeToWorkspaceEvents();
    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params) => {
      this.prepareForWorkspaceLoad(params['workspaceId']);
    });
  }

  ngAfterViewInit(): void {
    this.setBreadcrumb();
    this.themeService.updateContent({
      searchPanel: null,
      fullWidthContent: true,
    });
  }

  ngOnDestroy() {
    this.cleanupOnDestroy();
  }

  toggleTab(index: number) {
    this.tabIndex = index;
    this.isWorkspace = index === 0;
    this.isProject = index === 1;
    this.isUser = false;
    this.setBreadcrumb();
  }

  deleteWorkspace() {
    if (!this.canDeleteWorkspace) return;
    const title = this.translatePipe.transform(TRANSLATION_KEYS.WORKSPACE_DELETE_TITLE);
    const description = this.translatePipe.transform(TRANSLATION_KEYS.WORKSPACE_DELETE_CONFIRM, {
      name: this.workspace.name,
    });
    const confirmBtn = this.translatePipe.transform(TRANSLATION_KEYS.YES);
    const cancelBtn = this.translatePipe.transform(TRANSLATION_KEYS.NO);
    this.notificationService
      .open({ title, description, confirmBtn, cancelBtn })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirm: any) => {
        if (confirm) {
          this.performWorkspaceDelete();
        }
      });
  }

  save() {
    if (!this.canUpdateWorkspace || !this.workspaceService.formValue) return;
    this.headerForm = this.workspaceService.formValue;
    const updatedData = { ...this.workspace, ...this.headerForm.value };
    this.isPending = true;
    const brandId = localStorage.getItem(STORAGE_KEYS.BRAND_ID);
    this.workspaceService
      .editWorkspace(updatedData.id, updatedData, brandId)
      .pipe(
        finalize(() => (this.isPending = false)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: (result: any) => this.handleSaveSuccess(result),
        error: (error) => this.handleSaveError(error, updatedData),
      });
  }

  private subscribeToWorkspaceEvents() {
    EmitterService.get(ActiveWorkspaceUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (updates: any) => {
          this.activeWorkspace = updates.workspace;
          this.updateHeaderInfo();
          this.setBreadcrumb();
        },
      });

    EmitterService.get(WorkspaceAvatarUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((info: any) => {
        if (info?.avatarUrl) {
          this.activeWorkspace = this.activeWorkspace || ({} as WorkspaceDTO);
          this.activeWorkspace.avatarUrl = info.avatarUrl;
          this.updateHeaderInfo();
        }
      });

    EmitterService.get(WorkspaceUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: any) => {
        this.workspace.name = result.name;
        this.activeWorkspace = result;
        this.updateHeaderInfo();
      });
  }

  private prepareForWorkspaceLoad(workspaceId: string) {
    this.workspaceId = workspaceId;
    this.workspace = {};
    this.companyUsers = [];
    this.loadWorkspaceData();
  }

  private loadWorkspaceData() {
    const authUser = this.authService.getAuthUser();
    zip(
      this.workspaceService.getWorkspaceById(authUser.company.id, this.workspaceId, true, {
        redirectOnForbidden: true,
      }),
      this.userService.getUsersByCompanyId(authUser.company.id)
    )
      .pipe(take(1))
      .subscribe((data) => this.handleLoadedWorkspaceData(data));
  }

  private handleLoadedWorkspaceData(data: any[]) {
    const workspace = data[0];
    if (workspace) {
      this.workspace = workspace;
      this.workspaceService.setActiveWorkspace(this.workspace);
      this.checkUserAccess();
      this.activityLogService.activityLogAccess.next({
        hasAccess: true,
        level: ActivityLogLevels.WORKSPACE,
        workspaceId: this.workspaceId,
      });
    }
    if (data[1]) this.companyUsers = data[1];
    this.updateHeaderInfo();
    this.setBreadcrumb();
  }

  private checkUserAccess() {
    if (!this.workspace) {
      this.navigateToEntry();
      return;
    }

    if (this.permissionService.isAccountManager() || this.permissionService.canUpdateWorkspace()) {
      return;
    }

    // Membership is no longer serialized on the workspace payload — check it via the
    // workspace-users endpoint (the backend's own 403 on getWorkspaceById remains the
    // real enforcement; this eject is belt-and-braces).
    this.userService
      .getUserByWorkspaceId(this.authUser.company.id, this.workspaceId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (users) => {
          if (!users?.some((u) => u.username === this.authUser.username)) this.navigateToEntry();
        },
        error: () => this.navigateToEntry(),
      });
  }

  @WithPendingGuard
  private performWorkspaceDelete() {
    const user = this.authService.getAuthUser();
    this.isPending = true;
    this.workspaceService
      .deleteWorkspace(user.company.id, this.workspace.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => this.handleDeleteSuccess(),
        error: () => this.handleDeleteError(),
      });
  }

  private handleDeleteSuccess() {
    this.analyticService.clearAnalyticsCache();
    this.analyticService
      .getActiveBrandWorkspaces()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (workspaces) => {
          this.isPending = false;
          if (workspaces.length) {
            this.setNewActiveWorkspace(workspaces[0]);
          } else {
            this.unsetWorkspace();
          }
        },
        error: () => {
          this.isPending = false;
          this.unsetWorkspace();
        },
      });
  }

  private handleDeleteError() {
    this.isPending = false;
    this.overlayService.showError(
      this.translatePipe.transform(TRANSLATION_KEYS.WORKSPACE_DELETE_ERROR),
      UI.HEADER_ERROR_TITLE
    );
  }

  private unsetWorkspace() {
    this.workspaceService.unsetActiveWorkspace();
    EmitterService.get(WorkspaceUpdatedEvent).emit({});
    this.router.navigate([ROUTES.PROJECTS]).then(() => {});
  }

  private setNewActiveWorkspace(workspace: any) {
    this.workspaceService.setActiveWorkspace(workspace);
    EmitterService.get(WorkspaceUpdatedEvent).emit({});
    this.router.navigate([ROUTES.PROJECTS]).then(() => {});
  }

  private handleSaveSuccess(result: any) {
    EmitterService.get(WorkspaceUpdatedEvent).emit(result);
    this.activeWorkspace = result;
    this.updateHeaderInfo();
    this.workspace.name = this.headerForm.value.name;
    this.analyticService.clearAnalyticsCache();
    this.overlayService.showSuccess(
      this.translatePipe.transform(TRANSLATION_KEYS.OVERLAY_CHANGE_SAVE),
      UI.HEADER_SUCCESS_TITLE
    );
  }

  private handleSaveError(error: any, updatedData: any) {
    const errorCode = error?.errorCode;
    const messageKey =
      errorCode === ERROR_CODES.DUPLICATE_NAME
        ? TRANSLATION_KEYS.WORKSPACE_EDIT_DUPLICATE_ERROR
        : TRANSLATION_KEYS.WORKSPACE_EDIT_ERROR;
    this.overlayService.showError(
      this.translatePipe.transform(messageKey, { name: updatedData.name }),
      UI.HEADER_ERROR_TITLE
    );
  }

  private updateHeaderInfo() {
    if (!this.activeWorkspace) return;
    const headerUpdate: Partial<HeaderInfo> = {
      avatarImage: this.activeWorkspace.avatarUrl,
      avatarName: this.activeWorkspace.name,
      title: this.activeWorkspace.name,
    };
    this.themeService.updateHeaderInfo(headerUpdate);
  }

  setBreadcrumb() {
    const brandRaw = localStorage.getItem(STORAGE_KEYS.BRAND);
    const brand = brandRaw ? JSON.parse(brandRaw) : null;
    const pageLinks: MtmBreadcrumbLink[] = [
      { label: brand?.name, url: [ROUTES.ROOT] },
      { label: BREADCRUMB.WORKSPACES_LABEL, url: [ROUTES.WORKSPACES] },
      {
        label: this.activeWorkspace?.name,
        url: [ROUTES.WORKSPACE_CAMPAIGN_ROUTE, 'campaign-drive'],
        queryParams: { wid: this.cryptoService.encryptString(this.workspaceId) },
      },
      { label: BREADCRUMB.SETTINGS_LABEL },
    ];

    if (this.tabIndex === 0) {
      pageLinks.push({ label: BREADCRUMB.WORKSPACE_PERMISSIONS_LABEL });
    } else {
      pageLinks.push({ label: BREADCRUMB.PROJECT_PERMISSIONS_LABEL });
    }

    this.mtmBreadcrumbService.setLinks(pageLinks);

    if (this.activeWorkspace?.name)
      this.googleAnalyticsService.sendUserAnalyticsConfig('', this.activeWorkspace?.name, '');
  }

  private navigateToEntry() {
    this.router.navigate([ROUTES.ENTRY]).then(() => {});
  }

  private cleanupOnDestroy() {
    this.mtmBreadcrumbService.setLinks([]);
    this.themeService.updateContent({ searchPanel: null, fullWidthContent: false });
    this.activityLogService.activityLogAccess.next({ hasAccess: false });
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
